'use client';

import {styled, type CSSObject, type SxProps, type Theme} from '@mui/material/styles';
import type {
  OlympusFontFamily,
  OlympusFontSize,
  OlympusFontStyle,
  OlympusFontWeight,
  OlympusLineHeight,
} from '@olympus-frontend/tenant-config';
import type React from 'react';

export type FontSize = OlympusFontSize;
export type FontWeight = OlympusFontWeight;
export type LineHeight = OlympusLineHeight;
export type FontFamily = OlympusFontFamily;
export type FontStyle = OlympusFontStyle;
export type TypographyTag = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';

export interface TypographyProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  size?: FontSize;
  weight?: FontWeight;
  lineHeight?: LineHeight;
  family?: FontFamily;
  fontStyle?: FontStyle;
  textTransform?: 'uppercase' | 'capitalize';
  mobileSize?: FontSize;
  mobileWeight?: FontWeight;
  mobileLineHeight?: LineHeight;
  as?: TypographyTag;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
}

interface StyledProps {
  oSize: FontSize;
  oWeight: FontWeight;
  oLineHeight?: LineHeight;
  oFamily?: FontFamily;
  oFontStyle?: FontStyle;
  oTransform?: 'uppercase' | 'capitalize';
  oMobileSize?: FontSize;
  oMobileWeight?: FontWeight;
  oMobileLineHeight?: LineHeight;
}

const HEADING_SIZE = /^(display|headline|title)/;

function fontCss(theme: Theme, size: FontSize, weight: FontWeight, lineHeight?: LineHeight, family?: FontFamily): CSSObject {
  return {
    fontFamily: theme.font.family[family ?? (HEADING_SIZE.test(size) ? 'primary' : 'secondary')],
    fontSize: theme.font.size[size],
    fontWeight: theme.font.weight[weight],
    lineHeight: lineHeight ? theme.font.lineHeight[lineHeight] : 1.2,
  };
}

const PROP_NAMES = [
  'oSize',
  'oWeight',
  'oLineHeight',
  'oFamily',
  'oFontStyle',
  'oTransform',
  'oMobileSize',
  'oMobileWeight',
  'oMobileLineHeight',
  'sx',
  'as',
];

// `as` must stay excluded: forwarded to the DOM it renders <p as="h2"> instead of
// swapping the element, silently removing every semantic heading.
const Root = styled('p', {
  shouldForwardProp: propName => !PROP_NAMES.includes(propName as string),
})<StyledProps>(({theme, oSize, oWeight, oLineHeight, oFamily, oFontStyle, oTransform, oMobileSize, oMobileWeight, oMobileLineHeight}) => {
  const mobile = oMobileSize ?? oMobileWeight ?? oMobileLineHeight;
  return {
    margin: 0,
    color: theme.palette.colors.text.neutral.strong,
    ...fontCss(theme, oSize, oWeight, oLineHeight, oFamily),
    ...(oFontStyle && {fontStyle: oFontStyle}),
    ...(oTransform && {textTransform: oTransform}),
    ...(mobile && {
      [theme.breakpoints.down('md')]: fontCss(
        theme,
        oMobileSize ?? oSize,
        oMobileWeight ?? oWeight,
        oMobileLineHeight ?? (oMobileSize ? undefined : oLineHeight),
        oFamily,
      ),
    }),
  };
});

export function Typography({
  size = 'body-2',
  weight = 'regular',
  lineHeight,
  family,
  fontStyle,
  textTransform,
  mobileSize,
  mobileWeight,
  mobileLineHeight,
  as = 'p',
  children,
  ...rest
}: TypographyProps) {
  return (
    <Root
      as={as}
      oSize={size}
      oWeight={weight}
      oLineHeight={lineHeight}
      oFamily={family}
      oFontStyle={fontStyle}
      oTransform={textTransform}
      oMobileSize={mobileSize}
      oMobileWeight={mobileWeight}
      oMobileLineHeight={mobileLineHeight}
      {...rest}
    >
      {children}
    </Root>
  );
}

Typography.displayName = 'Typography';
